import React, { useState, useCallback, useRef, useEffect } from "react";
import SectionWrapper from "../components/SectionWrapper";
import { PiPhoneDisconnectFill } from "react-icons/pi";
import { SiWhatsapp } from "react-icons/si";
import { MdEmail } from "react-icons/md";
import ReCAPTCHA from "react-google-recaptcha";

// 👉 ADD THIS IMPORT
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const ContactPage = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [captchaToken, setCaptchaToken] = useState("");
  const [formData, setFormData] = useState({
    contactHeading: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    preferredTime: "",
    type: "",
  });
  const [emailTemplate, setEmailTemplate] = useState("");
  const [apiEndpoints, setApiEndpoints] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const recaptchaRef = useRef();

  useEffect(() => {
    fetch("/Template/email.txt")
      .then((res) => res.text())
      .then((data) => setEmailTemplate(data))
      .catch((err) => console.error("Error loading template:", err));

    fetch("/AllJson/apiendpoints.json")
      .then((res) => res.json())
      .then((data) => setApiEndpoints(data))
      .catch((err) => console.error("Error loading endpoints:", err));
  }, []);

  const handleVerify = useCallback((token) => setCaptchaToken(token), []);

  const resetCaptcha = () => {
    setCaptchaToken("");
    recaptchaRef.current?.reset();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!captchaToken) {
      alert("Please verify that you are not a robot.");
      setIsSubmitting(false);
      return;
    }

    if (!emailTemplate) {
      alert("Email template not loaded yet!");
      setIsSubmitting(false);
      return;
    }

    try {
      const emailType =
        activeTab === "callback" ? "Request Callback" : "General Query";

      let filledTemplate = emailTemplate;
      const mergedData = {
        ...formData,
        contactHeading: emailType,
        name:
          activeTab === "callback"
            ? formData.firstName
            : `${formData.firstName} ${formData.lastName}`.trim(),
        type: emailType,
        preferredTimeSection:
          activeTab === "callback"
            ? `Preferred time are ${formData.preferredTime}`
            : "",
      };

      Object.keys(mergedData).forEach((key) => {
        const regex = new RegExp(`{{${key}}}`, "g");
        filledTemplate = filledTemplate.replace(regex, mergedData[key] || "");
      });

      const payload = {
        fromMail: formData.email,
        emailSubject: emailType,
        emailBody: filledTemplate,
        entityName: "aegiscare",
      };

      const response = await fetch(apiEndpoints.enquiryUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          entity: "Aegis Care",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSubmitStatus("success");
        alert(
          activeTab === "callback"
            ? "Thank you! We will call you back soon."
            : "Thank you for your enquiry! We’ll reach out within 24 hours."
        );
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          message: "",
          preferredTime: "",
          type: "",
        });
        resetCaptcha();
      } else {
        const errText = await response.text();
        console.error("API Error:", errText);
        setSubmitStatus("error");
        alert("Something went wrong. Please try again later.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <main className="mt-12 md:mt-16 bg-maroon sm:px-6 lg:px-20">
      <SectionWrapper className="bg-maroon py-12 md:py-20 w-full">
        <header className="text-center mb-12 md:mb-16">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
            Get in Touch
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-200 max-w-xl mx-auto">
            Ready to start your care journey? We're here to help.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Info */}
          <section aria-labelledby="contact-info">
            <h2
              id="contact-info"
              className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-6 md:mb-8"
            >
              Contact Information
            </h2>
            <ul className="space-y-6">
              {[
                {
                  icon: <PiPhoneDisconnectFill className="text-maroon w-6 h-6" />,
                  title: "24/7 Helpline",
                  value: "+91 98102 52031",
                  link: "tel:+919810252031",
                },
                {
                  icon: <SiWhatsapp className="text-maroon w-6 h-6" />,
                  title: "WhatsApp",
                  value: "+91 98102 52031",
                  link: "https://wa.me/919810252031",
                },
                {
                  icon: <MdEmail className="text-maroon w-6 h-6" />,
                  title: "Email",
                  value: "info@aegiscare.co.in",
                  link: "mailto:info@aegiscare.co.in",
                },
              ].map((item, idx) => (
                <li key={idx} className="flex items-center">
                  <div className="bg-gold rounded-full w-12 h-12 flex items-center justify-center mr-4">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{item.title}</h3>
                    <p className="text-gray-200">
                      <a
                        href={item.link}
                        target={item.title === "WhatsApp" ? "_blank" : "_self"}
                        rel="noopener noreferrer"
                        className="underline focus:outline-none focus:ring-2 focus:ring-white"
                      >
                        {item.value}
                      </a>
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Contact Forms */}
          <section aria-labelledby="contact-form">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              {/* Tabs */}
              <div className="mb-4 flex flex-col sm:flex-row justify-center items-center sm:space-x-4 space-y-2 sm:space-y-0">
                <button
                  onClick={() => setActiveTab("general")}
                  className={`tab-btn ${
                    activeTab === "general"
                      ? "bg-maroon text-white"
                      : "bg-gray-200 text-maroon"
                  } px-6 py-2 rounded-full font-semibold focus:ring-2 focus:ring-maroon transition-colors duration-300`}
                >
                  General Query
                </button>
                <button
                  onClick={() => setActiveTab("callback")}
                  className={`tab-btn ${
                    activeTab === "callback"
                      ? "bg-maroon text-white"
                      : "bg-gray-200 text-maroon"
                  } px-6 py-2 rounded-full font-semibold focus:ring-2 focus:ring-maroon transition-colors duration-300`}
                >
                  Request Callback
                </button>
              </div>

              {/* Forms */}
              <form
                onSubmit={handleSubmit}
                className="space-y-4"
                aria-label={
                  activeTab === "callback"
                    ? "Request Callback Form"
                    : "General Enquiry Form"
                }
              >
                {activeTab === "general" && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="First Name"
                        required
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon"
                      />
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Last Name"
                        required
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon"
                      />
                    </div>

                    {/* ----------------------- */}
                    {/* PHONE INPUT (UPDATED) */}
                    {/* ----------------------- */}
                    <PhoneInput
                      country={"in"}
                      enableSearch={true}
                      value={formData.phone}
                      onChange={(value) =>
                        setFormData((p) => ({ ...p, phone: value }))
                      }
                      inputStyle={{
                        width: "100%",
                        height: "48px",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                      }}
                    />

                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email Address"
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon"
                    />

                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Your Message"
                      rows="4"
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon"
                    />
                  </>
                )}

                {activeTab === "callback" && (
                  <>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="Full Name"
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon"
                    />

                    {/* ----------------------- */}
                    {/* PHONE INPUT (UPDATED) */}
                    {/* ----------------------- */}
                    <PhoneInput
                      country={"in"}
                      enableSearch={true}
                      value={formData.phone}
                      onChange={(value) =>
                        setFormData((p) => ({ ...p, phone: value }))
                      }
                      inputStyle={{
                        width: "100%",
                        height: "48px",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                      }}
                    />

                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon"
                    >
                      <option value="">Preferred Time</option>
                      <option value="Morning (9 AM - 12 PM)">Morning</option>
                      <option value="Afternoon (12 PM - 5 PM)">Afternoon</option>
                      <option value="Evening (5 PM - 8 PM)">Evening</option>
                    </select>

                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Brief description of your needs"
                      rows="3"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon"
                    />
                  </>
                )}

                <ReCAPTCHA
                  sitekey="6Le82-krAAAAAJqdbcRPwQlXhDf5XS_HE-68-LsB"
                  ref={recaptchaRef}
                  onChange={handleVerify}
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full ${
                    activeTab === "callback"
                      ? "bg-gold text-maroon"
                      : "bg-maroon text-white"
                  } py-3 rounded-lg font-semibold hover:opacity-90 transition-all duration-300`}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : activeTab === "callback"
                    ? "Request Callback"
                    : "Send Message"}
                </button>
              </form>
            </div>
          </section>
        </div>
      </SectionWrapper>
    </main>
  );
};

export default ContactPage;
